
import { useState, useEffect, useMemo } from "react";
import { Shield, Globe, Info, AlertCircle, ChevronDown, ChevronRight, Plus, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNetworkRules, type NetworkRule } from "@/hooks/useNetworkRules";
import { useToast } from "@/hooks/use-toast";
import { useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { useLevelRuleScope } from "@/hooks/useLevelRuleScope";
import { AppIdDisplay } from "./AppIdDisplay";

interface LevelNetworkRulesEditModeProps {
  levelId: string;
  ruleType: "system-wide" | "app-specific";
  allowedApps?: any[];
}

interface GroupedAppRule {
  parentApp: {
    id: string;
    name: string;
    iconUrl?: string;
    bundleId: string;
  };
  rules: NetworkRule[];
}

export const LevelNetworkRulesEditMode = ({ 
  levelId, 
  ruleType, 
  allowedApps = [] 
}: LevelNetworkRulesEditModeProps) => {
  const [expandedApps, setExpandedApps] = useState<Set<string>>(new Set());
  const { data: allNetworkRules, isLoading } = useNetworkRules();
  const { toast } = useToast();
  
  // Get the app filter for this level with proper structure
  const appFilter = { default: true };
  const { scopedRuleIds, rulesData, isLoading: scopeLoading, hasError, errorMessage } = useLevelRuleScope(levelId, appFilter);
  const updateLevelRules = useConvexMutation(api.levels.rules.update);

  // Filter rules based on type - show ALL available rules in edit mode
  const filteredRules = useMemo(() => {
    return allNetworkRules?.filter(rule => {
      if (ruleType === "system-wide") {
        return rule.type === "General";
      } else {
        return rule.type === "App Specific";
      }
    }) || [];
  }, [allNetworkRules, ruleType]);

  // Initialize expanded apps with all parent apps expanded by default
  useEffect(() => {
    if (ruleType === "app-specific" && filteredRules.length > 0) {
      const parentAppIds = new Set<string>();
      filteredRules.forEach(rule => {
        if (rule.parentApp) {
          parentAppIds.add(rule.parentApp.id);
        }
      });
      console.log('Setting all parent apps expanded by default:', Array.from(parentAppIds));
      setExpandedApps(parentAppIds);
    }
  }, [filteredRules, ruleType]);

  const handleAddRule = async (ruleId: string) => {
    try {
      await updateLevelRules({
        level: levelId as any,
        add: [ruleId as any]
      });

      toast({
        title: "Rule Added",
        description: "Network rule has been added to this level.",
      });
    } catch (error) {
      console.error('Error adding rule to level:', error);
      toast({
        title: "Error",
        description: "Failed to add rule to level. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveRule = async (ruleId: string) => {
    try {
      await updateLevelRules({
        level: levelId as any,
        remove: [ruleId as any]
      });

      toast({
        title: "Rule Removed",
        description: "Network rule has been removed from this level.",
      });
    } catch (error) {
      console.error('Error removing rule from level:', error);
      toast({
        title: "Error",
        description: "Failed to remove rule from level. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleAppToggle = (appId: string) => {
    const newExpanded = new Set(expandedApps);
    if (newExpanded.has(appId)) {
      newExpanded.delete(appId);
    } else {
      newExpanded.add(appId);
    }
    setExpandedApps(newExpanded);
  };

  const groupedByParentApp = (rules: NetworkRule[]): GroupedAppRule[] => {
    const groupsMap = new Map<string, GroupedAppRule>();

    rules.forEach(rule => {
      if (rule.parentApp) {
        const key = rule.parentApp.id;
        if (!groupsMap.has(key)) {
          groupsMap.set(key, {
            parentApp: rule.parentApp,
            rules: []
          });
        }
        groupsMap.get(key)!.rules.push(rule);
      }
    });

    return Array.from(groupsMap.values());
  };

  if (isLoading || scopeLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-gray-500">Loading network rules...</div>
      </div>
    );
  }

  // Show error state if there's an issue with the level ID
  if (hasError) {
    return (
      <Card>
        <CardContent className="p-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              {errorMessage || 'Unable to load network rules for this level. Please check the level configuration.'}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (filteredRules.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <Shield className="w-12 h-12 text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No {ruleType === "system-wide" ? "System-Wide" : "App-Specific"} Rules
          </h3>
          <p className="text-gray-500 text-center">
            {ruleType === "system-wide" 
              ? "There are no system-wide network rules available to configure."
              : "There are no app-specific rules available to configure."
            }
          </p>
        </CardContent>
      </Card>
    );
  }

  // For app-specific rules, group by parent app
  if (ruleType === "app-specific") {
    const parentAppGroups = groupedByParentApp(filteredRules);

    return (
      <ScrollArea className="h-[600px]">
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[400px]">Rule Name</TableHead>
                  <TableHead className="w-[100px]">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {parentAppGroups.map((group) => (
                  <>
                    {/* Parent App Header Row */}
                    <TableRow 
                      key={`app-${group.parentApp.id}`}
                      className="hover:bg-gray-50 cursor-pointer transition-colors bg-gray-25 h-12"
                      onClick={() => handleAppToggle(group.parentApp.id)}
                    >
                      <TableCell className="py-2">
                        <div className="flex items-center">
                          {expandedApps.has(group.parentApp.id) ? (
                            <ChevronDown className="w-4 h-4 mr-2 text-gray-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 mr-2 text-gray-500" />
                          )}
                          <AppIdDisplay appId={group.parentApp.id} />
                        </div>
                      </TableCell>
                      <TableCell className="py-2"></TableCell>
                    </TableRow>

                    {/* Expanded Rules */}
                    {expandedApps.has(group.parentApp.id) && group.rules.map((rule) => {
                      const isInScope = scopedRuleIds.has(rule.id);
                      
                      return (
                        <TableRow key={`rule-${rule.id}`} className="hover:bg-gray-50 h-10">
                          <TableCell className="py-1">
                            <div className="flex items-center pl-8">
                              <div className="flex items-center gap-2">
                                <div>
                                  <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                                </div>
                                {rule.description && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                      </TooltipTrigger>
                                      <TooltipContent>
                                        <p className="max-w-xs">{rule.description}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="py-1">
                            {isInScope ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleRemoveRule(rule.id)}
                                className="h-6 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                              >
                                <Minus className="w-3 h-3 mr-1" />
                                Remove
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleAddRule(rule.id)}
                                className="h-6 px-2 text-xs text-green-600 border-green-200 hover:bg-green-50"
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Add
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </ScrollArea>
    );
  }

  // For system-wide rules, show the flat table
  return (
    <ScrollArea className="h-[600px]">
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[400px]">Rule Name</TableHead>
                <TableHead className="w-[100px]">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map((rule) => {
                const isInScope = scopedRuleIds.has(rule.id);
                
                return (
                  <TableRow key={rule.id} className="hover:bg-gray-50 h-10">
                    <TableCell className="py-2">
                      <div className="flex items-center">
                        <div className="flex items-center gap-2">
                          <div>
                            <div className="font-medium text-gray-900 text-sm">{rule.name}</div>
                          </div>
                          {rule.description && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">{rule.description}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="py-2">
                      {isInScope ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveRule(rule.id)}
                          className="h-6 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Minus className="w-3 h-3 mr-1" />
                          Remove
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddRule(rule.id)}
                          className="h-6 px-2 text-xs text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </ScrollArea>
  );
};
